import React, { useContext, useEffect, useState } from 'react';
import {
  Autocomplete, Box, TextField, Typography,
} from '@mui/material';
import { useDebounce } from 'use-debounce';
import { Vendor, VendorType } from 'API';
import VendorService from 'services/vendor';
import VendorModal from 'components/modals/VendorModal';
import { resolvers } from 'shared/enums';
import AlertContext from 'shared/contexts/AlertContext';
import { DICTIONARY } from 'shared/constants';

const ADD_NEW_VENDOR_ID = '__add_new_vendor__';

const addNewOption = (type: VendorType, name: string): Vendor => ({
  __typename: 'Vendor',
  id: ADD_NEW_VENDOR_ID,
  name,
  type,
} as Vendor);

interface FieldProps {
  type: VendorType;
  value?: Vendor | null;
  disabled?: boolean;
  onChange: (vendor: Vendor | null) => void;
}

const VendorTypeAutocomplete: React.FC<FieldProps> = ({
  type, value, disabled, onChange,
}: FieldProps) => {
  const [, setAlert] = useContext(AlertContext);
  const [textInput, setTextInput] = useState('');
  const [debouncedTextInput] = useDebounce(textInput, 200);
  const [isSearching, setIsSearching] = useState(false);
  const [options, setOptions] = useState<Array<Vendor>>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setIsSearching(true);

      try {
        const response = await VendorService.searchByName(debouncedTextInput, type, 20);

        if (!ignore) {
          setOptions(
            (response.filter(Boolean) as Vendor[])
              .sort((a, b) => a.name.localeCompare(b.name)),
          );
        }
      } catch (err: any) {
        // Say the search failed. Falling through to an empty list reads as
        // "no such vendor" and pushes the user into creating a duplicate.
        if (!ignore) {
          setOptions([]);
          setAlert([err?.message || DICTIONARY.DefaultError, 'error']);
        }
      } finally {
        if (!ignore) {
          setIsSearching(false);
        }
      }
    };

    if (debouncedTextInput) {
      load();
    } else {
      setOptions([]);
    }

    return () => {
      ignore = true;
    };
  }, [debouncedTextInput, type]);

  const label = type === VendorType.COMPANY ? resolvers.VendorType(VendorType.COMPANY) : resolvers.VendorType(VendorType.CO_COUNSEL);

  return (
    <>
      <Autocomplete
        disabled={disabled}
        filterOptions={(opts) => (
          textInput.trim() ? [...opts, addNewOption(type, textInput.trim())] : opts
        )}
        options={options}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, v) => option.id === v.id}
        noOptionsText={
          isSearching ? DICTIONARY.AutocompleteSearching : DICTIONARY.AutocompletePrompt
        }
        value={value || null}
        onChange={(_, newValue) => {
          if (newValue?.id === ADD_NEW_VENDOR_ID) {
            setAddModalOpen(true);

            return;
          }

          onChange(newValue);
        }}
        renderOption={({ key, ...props }, option) => (
          <Box component="li" key={key} {...props}>
            <Typography color={option.id === ADD_NEW_VENDOR_ID ? 'primary' : undefined}>
              {option.id === ADD_NEW_VENDOR_ID
                ? `${type === VendorType.COMPANY ? DICTIONARY.AddNewVendor : DICTIONARY.AddNewCoCounsel}: "${option.name}"`
                : option.name}
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            label={label}
            onChange={(e) => setTextInput(e.target.value)}
          />
        )}
      />
      <VendorModal
        open={addModalOpen}
        setOpen={setAddModalOpen}
        type={type}
        initialName={textInput.trim()}
        onCreated={(vendor) => {
          setTextInput('');
          onChange(vendor);
        }}
      />
    </>
  );
};

interface Props {
  value?: Vendor | null;
  onChange: (vendor: Vendor | null) => void;
  disabled?: boolean;
}

const VendorCoCounselPicker: React.FC<Props> = ({ value, onChange, disabled }: Props) => {
  const isVendorSelected = value?.type === VendorType.COMPANY;
  const isCoCounselSelected = value?.type === VendorType.CO_COUNSEL;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '1rem' }}>
      <Box sx={{ flex: 1 }}>
        <VendorTypeAutocomplete
          type={VendorType.COMPANY}
          value={isVendorSelected ? value : null}
          disabled={disabled || isCoCounselSelected}
          onChange={onChange}
        />
      </Box>
      <Typography sx={{ paddingBottom: '0.5rem' }}>- OR -</Typography>
      <Box sx={{ flex: 1 }}>
        <VendorTypeAutocomplete
          type={VendorType.CO_COUNSEL}
          value={isCoCounselSelected ? value : null}
          disabled={disabled || isVendorSelected}
          onChange={onChange}
        />
      </Box>
    </Box>
  );
};

export default VendorCoCounselPicker;
